import * as React from 'react';

/**
 * Which site a screen is drawn in — the Advisor Portal, the Team Portal, or the front door both
 * pass through (`docs/superpowers/specs/2026-09-28-two-portals-design.md`, §1).
 *
 * Every component in `src/design` is zone-neutral: it reads the zone from here, or the zone's
 * tokens from the CSS custom properties its shell sets on `[data-zone]`, and never imports a
 * shell. That is what keeps the two sites separable — each shell provides a zone, the design
 * system renders into it, and nothing in either shell needs the other.
 *
 * The context carries what a page cannot know by itself: the prefix its document title takes, what
 * the shell does with a record the page has just read (the Team Portal's recent records; the
 * portal does nothing), how much room the page may ask for, and where a page that takes over the
 * shell's bar draws into it (`FocusBar` — the portal's survey).
 */
export type Zone = 'portal' | 'team' | 'front-door';

/** What the tab says before the page's own title — so that a row of tabs says which site each is. */
export const ZONE_TITLE: Record<Zone, string> = {
  portal: 'Advisor Portal',
  team: 'Team Portal',
  'front-door': 'Veritant'
};

/** The kinds of record a visit names, one list for the type and the stored visit's check. */
export const RECORD_VISIT_KINDS = [
  'person',
  'team',
  'firm',
  'org',
  'register-firm',
  'response',
  'account'
] as const;

export type RecordVisitKind = (typeof RECORD_VISIT_KINDS)[number];

/** A record a page opened — a person, a team, a firm or another organisation by its record type
 *  (SR-414), a firm of the SEC ADV register, a response, an account — as the shell may keep it. */
export interface IRecordVisit {
  kind: RecordVisitKind;
  /** The address that opens it again, as the page was reached. */
  path: string;
  /** What the page calls it: a display name, a legal name, an address. */
  label: string;
}

/**
 * How much of the screen a page asks for: `split`, a list beside a record, which wants the room a
 * full rail takes on a laptop; `full`, a workspace an author stays in (the version editor), which
 * wants it on any screen. The Team Portal collapses its rail while one is asked; anywhere else
 * this is a no-op.
 */
export type WorkspaceWidth = 'split' | 'full';

interface IZoneContext {
  zone: Zone;
  /** Whose screen this is — an account id — so a per-viewer preference is kept per viewer. */
  viewer?: string;
  onRecord?: (record: IRecordVisit) => void;
  onWorkspace?: (width: WorkspaceWidth) => () => void;
  /** Where a focused page draws its bar — the element the shell keeps for it, once mounted. */
  focusSlot?: Nullable<HTMLElement>;
  /** The shell swaps its navigation for the slot while a caller holds focus. */
  onFocus?: () => () => void;
  /** The app bar a work page's compact bar is drawn over (`condense.tsx`). */
  appBar?: IAppBar;
}

/** A page's read that *Refresh* asks again: held off while it is out, "updated hh:mm" after. */
export interface IAppRefresh {
  reading: boolean;
  readAt: Nullable<Date>;
  onClick: () => void;
}

/** What the shell's app bar lends a page's compact bar. */
export interface IAppBar {
  /** The app bar's element, the bar drawn over its row; null until it is mounted. */
  host: Nullable<HTMLElement>;
  /** The find palette's name — the 🔍 standing for its box — and its opener. */
  findLabel: string;
  onFind: () => void;
  /** The recent records' menu as the bar draws it — an icon — last in the row, as in the app bar. */
  recent?: React.ReactNode;
  /** The page's *Refresh*, drawn by the app bar left of its search — one place on every page — and
   *  by a compact bar there too; null while the page has none. */
  refresh?: Nullable<IAppRefresh>;
  /** A page hands its *Refresh* to the app bar, and takes it back (null) as it goes. */
  setRefresh?: (refresh: Nullable<IAppRefresh>) => void;
  /** Hides the app bar's own row under the bar, and shows it again. */
  setCompact: (compact: boolean) => void;
}

const ZoneContext = React.createContext<IZoneContext>({ zone: 'front-door' });

export function ZoneProvider({
  zone,
  viewer,
  onRecord,
  onWorkspace,
  focusSlot,
  onFocus,
  appBar,
  children
}: {
  zone: Zone;
  viewer?: string;
  onRecord?: (record: IRecordVisit) => void;
  onWorkspace?: (width: WorkspaceWidth) => () => void;
  focusSlot?: Nullable<HTMLElement>;
  onFocus?: () => () => void;
  appBar?: IAppBar;
  children: React.ReactNode;
}) {
  const value = React.useMemo(
    () => ({ zone, viewer, onRecord, onWorkspace, focusSlot, onFocus, appBar }),
    [zone, viewer, onRecord, onWorkspace, focusSlot, onFocus, appBar]
  );
  return <ZoneContext.Provider value={value}>{children}</ZoneContext.Provider>;
}

export function useZone(): Zone {
  return React.useContext(ZoneContext).zone;
}

/** The viewer a preference is kept for, or `''` where the zone names nobody. */
export function useViewer(): string {
  return React.useContext(ZoneContext).viewer ?? '';
}

/** Asks the shell for the room `width` needs, for as long as the caller is mounted and
 *  `active`. */
export function useWorkspaceWidth(width: WorkspaceWidth, active = true): void {
  const { onWorkspace } = React.useContext(ZoneContext);
  React.useEffect(() => {
    if (!active || !onWorkspace) return;
    return onWorkspace(width);
  }, [width, active, onWorkspace]);
}

/** The shell's app bar, where the zone has one. */
export function useAppBar(): Nullable<IAppBar> {
  return React.useContext(ZoneContext).appBar ?? null;
}

/**
 * Holds the shell's bar for as long as the caller is mounted, and answers the element to draw
 * into — `null` until the shell has mounted it, and wherever the zone has no bar to give.
 */
export function useFocusSlot(): Nullable<HTMLElement> {
  const { focusSlot, onFocus } = React.useContext(ZoneContext);
  React.useEffect(() => onFocus?.(), [onFocus]);
  return focusSlot ?? null;
}

/**
 * Puts the shell's zone on `<html>`, for as long as the shell is mounted.
 *
 * On the root element rather than the shell's own `div`, because a menu, a dialog or a tooltip is
 * rendered by Radix into `document.body`, outside any shell — and its buttons, fields and focus
 * ring must still take the zone's tokens. Only one shell is mounted at a time, so the attribute
 * is simply the current one's; a frame drawn in another zone inside a screen (the builder's preview,
 * drawn as the Advisor Portal) sets `data-zone` on itself, and the nearer declaration wins.
 *
 * A layout effect, so the first paint is already in the zone's colours.
 */
export function useZoneRoot(zone: Zone): void {
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.zone = zone;
    return () => {
      if (root.dataset.zone === zone) delete root.dataset.zone;
    };
  }, [zone]);
}

/**
 * Sets the tab's title — `Team Portal | People` — for as long as the calling screen is mounted.
 *
 * Until the two portals the title never changed from the one in `index.html`, so a row of tabs
 * could not be told apart, least of all a staff member's People tab from an advisor's survey
 * open beside it. `null` leaves the zone's name alone, for a screen still loading what it is
 * called.
 */
export function useDocumentTitle(title: Nullable<string>): void {
  const zone = useZone();
  React.useEffect(() => {
    const prefix = ZONE_TITLE[zone];
    document.title = title ? `${prefix} | ${title}` : prefix;
  }, [title, zone]);
}

/**
 * Tells the shell that a record has been read and is on screen, once per record. The Team Portal
 * keeps the last few as its recent records; anywhere else this is a no-op. `null` while the
 * record is still loading, or when the read found nothing — a record that does not exist is not
 * somewhere to come back to.
 */
export function useRecordVisit(record: Nullable<IRecordVisit>): void {
  const { onRecord } = React.useContext(ZoneContext);
  const key = record ? `${record.kind}\u0001${record.path}\u0001${record.label}` : null;
  const latest = React.useRef(record);
  latest.current = record;
  React.useEffect(() => {
    if (key && latest.current && onRecord) onRecord(latest.current);
  }, [key, onRecord]);
}
